import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetSuggestions as BLGetSuggestions,
    GetActivePlaylist as BLGetActivePlaylist,
    GetSettings as BLGetSettings,
    CreateManualSong as BLCreateManualSong,
    ToggleFavorite as BLToggleFavorite,
    ToggleArchive as BLToggleArchive,
    DeleteSuggestion as BLDeleteSuggestion,
    ReorderPlaylist as BLReorderPlaylist,
    SetNowPlaying as BLSetNowPlaying,
    TogglePlayed as BLTogglePlayed,
    ToggleSubmissionActive as BLToggleSubmissionActive,
    ResetDynamic as BLResetDynamic
} from '../../../../utilities/BL/Admin/Overlays/Playlist';
import {
    ConnectWebSocketForPlaylistAdmin,
    DisconnectWebSocketForPlaylistAdmin
} from '../../../../utilities/WS/Admin/PlaylistWS';
import { PlaylistSuggestionsTable } from './PlaylistSuggestionsTable';
import { PlaylistActiveList } from './PlaylistActiveList';
import { PlaylistManualSongModal } from './PlaylistManualSongModal';

/**
 * CONSOLA OPERATIVA DE CABINA DE SONIDO (DJ BOARD)
 * [HU-05] Arma la Playlist
 * 
 * Tablero principal a pantalla completa para el DJ / Operador de sonido en la ruta '/admin/playlist/cabina'.
 * Integración en tiempo real con SignalR (PlaylistWS), carga reactiva vía BL,
 * control dual de Bandeja de Entrada (izq) y Playlist Activa (der),
 * modales de confirmación para acciones críticas y herramientas especializadas de DJ.
 */
export const PlaylistSpecialBoard = ({ roomId: propRoomId = null }) => {
    const navigate = useNavigate();
    const { showMessage } = useFloatMessage();

    // -------------------------------------------------------------------------
    // 1. RESOLUCIÓN DE IDENTIFICADOR DE SALA (ROOM ID)
    // -------------------------------------------------------------------------
    const roomId = useMemo(() => {
        if (propRoomId) return propRoomId;
        const searchParams = new URLSearchParams(window.location.search);
        const urlRoomId = searchParams.get('roomId') || searchParams.get('room');
        if (urlRoomId) return urlRoomId;
        const storedRoomId = localStorage.getItem('interaktia_room_id') || localStorage.getItem('roomId');
        return storedRoomId || '';
    }, [propRoomId]);

    // -------------------------------------------------------------------------
    // 2. ESTADOS DE DATOS PRINCIPALES
    // -------------------------------------------------------------------------
    const [suggestions, setSuggestions] = useState([]);
    const [activePlaylist, setActivePlaylist] = useState([]);
    const [settings, setSettings] = useState(null);
    const [nowPlayingSong, setNowPlayingSong] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [wsStatus, setWsStatus] = useState('connecting'); // 'connecting', 'connected', 'reconnecting', 'error', 'closed'
    const [isFullscreen, setIsFullscreen] = useState(false);

    // -------------------------------------------------------------------------
    // 3. ESTADOS DE MODALES
    // -------------------------------------------------------------------------
    // Modal de Confirmación genérico (Confirm.js)
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        actionToYesButton: async () => ({ success: true }),
        textYesButton: 'Sí, confirmar',
        textNoButton: 'Cancelar',
        confirmData: null
    });

    // Modal de Creación de Canción Manual (PlaylistManualSongModal)
    const [showManualModal, setShowManualModal] = useState(false);

    // -------------------------------------------------------------------------
    // 4. CARGA INICIAL DE DATOS VÍA BL
    // -------------------------------------------------------------------------
    const loadInitialData = useCallback(async (showNotification = false) => {
        setIsLoading(true);
        try {
            const [suggestionsRes, activeRes, settingsRes] = await Promise.all([
                BLGetSuggestions({ roomId, includeArchived: true }),
                BLGetActivePlaylist({ roomId }),
                BLGetSettings({ roomId })
            ]);

            if (suggestionsRes.success && Array.isArray(suggestionsRes.data)) {
                setSuggestions(suggestionsRes.data);
            }

            if (activeRes.success && Array.isArray(activeRes.data)) {
                setActivePlaylist(activeRes.data);
                // Determinar Now Playing desde la lista activa
                const currentLive = activeRes.data.find(item => item.isNowPlaying);
                if (currentLive) {
                    setNowPlayingSong(currentLive);
                } else {
                    setNowPlayingSong(null);
                }
            }

            if (settingsRes.success && settingsRes.data) {
                setSettings(settingsRes.data);
            }

            if (showNotification) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '🔄 Datos de cabina actualizados correctamente.'
                });
            }
        } catch (error) {
            console.error('[PlaylistSpecialBoard] Error al cargar datos iniciales:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al consultar los datos de cabina de sonido.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [roomId, showMessage]);

    // -------------------------------------------------------------------------
    // 5. CONEXIÓN EN TIEMPO REAL CON SIGNALR (PlaylistWS)
    // -------------------------------------------------------------------------
    useEffect(() => {
        // Carga inicial al montar
        loadInitialData(false);

        // Handler reactivo de mensajes entrantes de SignalR
        const handleWsMessage = (envelope) => {
            if (!envelope) return;
            const eventType = envelope.type || '';
            const payload = envelope.payload || envelope.data;

            console.log(`[PlaylistSpecialBoard] Evento WS recibido: "${eventType}"`, payload);

            switch (eventType) {
                case 'suggestion_received': {
                    if (!payload || !payload.id) return;
                    setSuggestions((prev) => {
                        const exists = prev.some(item => item.id === payload.id);
                        if (exists) {
                            return prev.map(item => item.id === payload.id ? { ...item, ...payload } : item);
                        }
                        return [payload, ...prev];
                    });

                    // Si viene marcada como favorita directamente
                    if (payload.isFavorite) {
                        setActivePlaylist(prev => {
                            const existsInActive = prev.some(item => item.id === payload.id);
                            return existsInActive ? prev : [...prev, payload];
                        });
                    }

                    showMessage({
                        type: MESSAGE_TYPE.INFO,
                        text: `🎵 Nueva sugerencia: "${payload.title}"`
                    });
                    break;
                }

                case 'vote_added': {
                    if (!payload || !payload.suggestionId) return;
                    const suggestionId = payload.suggestionId;
                    const newVoteCount = payload.newVoteCount || payload.voteCount || 1;

                    setSuggestions(prev =>
                        prev.map(item =>
                            item.id === suggestionId ? { ...item, voteCount: newVoteCount } : item
                        )
                    );

                    setActivePlaylist(prev =>
                        prev.map(item =>
                            item.id === suggestionId ? { ...item, voteCount: newVoteCount } : item
                        )
                    );
                    break;
                }

                case 'suggestion_updated': {
                    if (!payload || !payload.id) return;
                    setSuggestions(prev =>
                        prev.map(item => item.id === payload.id ? { ...item, ...payload } : item)
                    );

                    setActivePlaylist(prev => {
                        if (payload.isFavorite) {
                            const exists = prev.some(item => item.id === payload.id);
                            return exists
                                ? prev.map(item => item.id === payload.id ? { ...item, ...payload } : item)
                                : [...prev, payload];
                        } else {
                            return prev.filter(item => item.id !== payload.id);
                        }
                    });

                    if (payload.isNowPlaying !== undefined) {
                        if (payload.isNowPlaying) {
                            setNowPlayingSong(payload);
                        } else {
                            setNowPlayingSong(current => (current && current.id === payload.id ? null : current));
                        }
                    }
                    break;
                }

                case 'suggestion_archived': {
                    if (!payload || !payload.suggestionId) return;
                    const { suggestionId, isArchived } = payload;
                    setSuggestions(prev =>
                        prev.map(item =>
                            item.id === suggestionId ? { ...item, isArchived } : item
                        )
                    );
                    if (isArchived) {
                        setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                        setNowPlayingSong(current => (current && current.id === suggestionId ? null : current));
                    }
                    break;
                }

                case 'suggestion_deleted': {
                    if (!payload || !payload.suggestionId) return;
                    const { suggestionId } = payload;
                    setSuggestions(prev => prev.filter(item => item.id !== suggestionId));
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                    setNowPlayingSong(current => (current && current.id === suggestionId ? null : current));
                    break;
                }

                case 'playlist_updated': {
                    if (Array.isArray(payload)) {
                        setActivePlaylist(payload);
                        const live = payload.find(item => item.isNowPlaying);
                        setNowPlayingSong(live || null);
                    }
                    break;
                }

                case 'now_playing_changed': {
                    if (payload && payload.id) {
                        setNowPlayingSong(payload);
                        setActivePlaylist(prev =>
                            prev.map(item => ({
                                ...item,
                                isNowPlaying: item.id === payload.id
                            }))
                        );
                        setSuggestions(prev =>
                            prev.map(item => ({
                                ...item,
                                isNowPlaying: item.id === payload.id
                            }))
                        );
                    } else {
                        setNowPlayingSong(null);
                        setActivePlaylist(prev =>
                            prev.map(item => ({
                                ...item,
                                isNowPlaying: false
                            }))
                        );
                        setSuggestions(prev =>
                            prev.map(item => ({
                                ...item,
                                isNowPlaying: false
                            }))
                        );
                    }
                    break;
                }

                case 'settings_updated': {
                    if (payload) {
                        setSettings(payload);
                    }
                    break;
                }

                case 'dynamic_reset': {
                    showMessage({
                        type: MESSAGE_TYPE.WARNING,
                        text: '⚠️ La dinámica ha sido reiniciada en la sala.'
                    });
                    loadInitialData(false);
                    break;
                }

                default:
                    break;
            }
        };

        // Conectar al Hub de SignalR
        ConnectWebSocketForPlaylistAdmin(
            handleWsMessage,
            (newStatus) => setWsStatus(newStatus),
            roomId
        );

        return () => {
            DisconnectWebSocketForPlaylistAdmin();
        };
    }, [roomId, loadInitialData, showMessage]);

    // -------------------------------------------------------------------------
    // 6. ACCIONES DE GESTIÓN: FAVORITOS / ARCHIVAR / ELIMINAR
    // -------------------------------------------------------------------------
    const handleToggleFavorite = async (suggestionId, isFavorite) => {
        try {
            // Optimistic update
            setSuggestions(prev =>
                prev.map(item =>
                    item.id === suggestionId ? { ...item, isFavorite } : item
                )
            );

            const res = await BLToggleFavorite({ roomId, suggestionId, isFavorite });
            if (res.success) {
                // Actualizar la lista activa
                if (isFavorite) {
                    const target = suggestions.find(s => s.id === suggestionId);
                    if (target) {
                        setActivePlaylist(prev => {
                            const exists = prev.some(item => item.id === suggestionId);
                            return exists ? prev : [...prev, { ...target, isFavorite: true }];
                        });
                    }
                } else {
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                }
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isFavorite ? '⭐ Pista añadida a la Playlist Activa' : 'Pista retirada de la Playlist Activa'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar favorito.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al modificar favorito.' });
            loadInitialData(false);
        }
    };

    const handleToggleArchive = async (suggestionId, isArchived) => {
        try {
            setSuggestions(prev =>
                prev.map(item =>
                    item.id === suggestionId ? { ...item, isArchived } : item
                )
            );
            if (isArchived) {
                setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
            }

            const res = await BLToggleArchive({ roomId, suggestionId, isArchived });
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isArchived ? '📦 Pista archivada' : '📦 Pista desarchivada'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al archivar sugerencia.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al archivar.' });
            loadInitialData(false);
        }
    };

    const handleDeleteSuggestion = (suggestionId, title = '') => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas eliminar permanentemente la pista "${title || 'seleccionada'}"?`,
            textYesButton: 'Sí, Eliminar',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await BLDeleteSuggestion({ roomId, suggestionId, softDelete: true });
                if (res.success) {
                    setSuggestions(prev => prev.filter(item => item.id !== suggestionId));
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                    setNowPlayingSong(current => (current && current.id === suggestionId ? null : current));
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '🗑️ Pista eliminada con éxito.' });
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al eliminar pista.' });
                }
                return res;
            },
            confirmData: { suggestionId }
        });
        setShowConfirmModal(true);
    };

    const handleRemoveFromActive = (suggestionId, title = '') => {
        setConfirmConfig({
            message: `¿Deseas quitar "${title || 'esta pista'}" de la playlist activa de cabina?`,
            textYesButton: 'Sí, Quitar',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await BLToggleFavorite({ roomId, suggestionId, isFavorite: false });
                if (res.success) {
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                    setSuggestions(prev =>
                        prev.map(item => item.id === suggestionId ? { ...item, isFavorite: false } : item)
                    );
                    setNowPlayingSong(current => (current && current.id === suggestionId ? null : current));
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pista retirada de la playlist activa.' });
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al quitar de la playlist.' });
                }
                return res;
            },
            confirmData: { suggestionId }
        });
        setShowConfirmModal(true);
    };

    // -------------------------------------------------------------------------
    // 7. ACCIONES DE REPRODUCCIÓN (NOW PLAYING / PLAYED / REORDER)
    // -------------------------------------------------------------------------
    const handleSetNowPlaying = async (suggestionId) => {
        try {
            const res = await BLSetNowPlaying({ roomId, suggestionId });
            if (res.success) {
                if (suggestionId) {
                    const target = activePlaylist.find(item => item.id === suggestionId) || suggestions.find(s => s.id === suggestionId);
                    setNowPlayingSong(target || null);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `▶️ EN VIVO: "${target?.title || 'Canción'}"`
                    });
                } else {
                    setNowPlayingSong(null);
                    showMessage({
                        type: MESSAGE_TYPE.INFO,
                        text: '⏹️ Reproducción en vivo detenida.'
                    });
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al establecer Now Playing.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al cambiar Now Playing.' });
        }
    };

    const handleTogglePlayed = async (suggestionId, isPlayed) => {
        try {
            // Optimistic update
            setActivePlaylist(prev =>
                prev.map(item => item.id === suggestionId ? { ...item, isPlayed } : item)
            );
            setSuggestions(prev =>
                prev.map(item => item.id === suggestionId ? { ...item, isPlayed } : item)
            );

            const res = await BLTogglePlayed({ roomId, suggestionId, isPlayed });
            if (!res.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar estado de reproducción.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cambiar estado de reproducción.' });
            loadInitialData(false);
        }
    };

    const handleReorderPlaylist = async (orderedIds) => {
        try {
            // Reordenar localmente
            const mapById = new Map(activePlaylist.map(item => [item.id, item]));
            const newOrdered = orderedIds.map(id => mapById.get(id)).filter(Boolean);
            setActivePlaylist(newOrdered);

            const res = await BLReorderPlaylist({ roomId, orderedIds });
            if (!res.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar el nuevo orden.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al reordenar la playlist.' });
            loadInitialData(false);
        }
    };

    // -------------------------------------------------------------------------
    // 8. CONTROLES GENERALES DE CABINA: RECEPCIÓN Y REINICIO
    // -------------------------------------------------------------------------
    const handleToggleSubmissionActive = async () => {
        const currentActive = settings ? settings.isSubmissionActive : true;
        const newActive = !currentActive;
        try {
            const res = await BLToggleSubmissionActive({ roomId, isActive: newActive });
            if (res.success) {
                setSettings(prev => prev ? { ...prev, isSubmissionActive: newActive } : { isSubmissionActive: newActive });
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: newActive ? '🟢 Recepción de sugerencias ABIERTA a aficionados' : '🔴 Recepción de sugerencias PAUSADA'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar recepción.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cambiar estado de recepción.' });
        }
    };

    const handleResetDynamic = () => {
        setConfirmConfig({
            message: '⚠️ ¿Estás seguro de que deseas REINICIAR LA DINÁMICA de Arma la Playlist? Todas las sugerencias activas se archivarán y se limpiará la cola de cabina.',
            textYesButton: 'Sí, Reiniciar Dinámica',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await BLResetDynamic({ roomId });
                if (res.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '🔄 Dinámica reiniciada con éxito.' });
                    await loadInitialData(false);
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al reiniciar la dinámica.' });
                }
                return res;
            },
            confirmData: {}
        });
        setShowConfirmModal(true);
    };

    // -------------------------------------------------------------------------
    // 9. MODO PANTALLA COMPLETA
    // -------------------------------------------------------------------------
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
        } else {
            document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
        }
    };

    // Active playlist IDs list for matching in left table
    const activePlaylistIds = useMemo(() => {
        return activePlaylist.map(item => item.id);
    }, [activePlaylist]);

    // -------------------------------------------------------------------------
    // 10. RENDERIZADO PRINCIPAL
    // -------------------------------------------------------------------------
    const isSubmissionActive = settings ? settings.isSubmissionActive : true;

    return (
        <div className={`DJBoard ${isFullscreen ? 'DJBoard--Fullscreen' : ''}`}>
            {/* --- HEADER PRINCIPAL DE CABINA --- */}
            <header className="DJBoard__Header">
                <div className="DJBoard__Header__Left">
                    {/* Botón Volver */}
                    <button
                        type="button"
                        className="DJBoard__Header__BackBtn"
                        onClick={() => navigate('/admin/dashboard')}
                        title="Volver al Dashboard"
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>

                    {/* Título y Branding */}
                    <div className="DJBoard__Header__Branding">
                        <div className="d-flex align-items-center gap-2">
                            <span className="DJBoard__Header__DjIcon">🎧</span>
                            <h1 className="DJBoard__Header__Title">Cabina de Sonido / DJ Board</h1>
                            <span className="DJBoard__Header__Badge">Arma la Playlist</span>
                        </div>
                        <span className="DJBoard__Header__Subtitle">
                            Consola Operativa en Tiempo Real | Interaktia-V2
                        </span>
                    </div>
                </div>

                {/* KPI Chips y Estado SignalR */}
                <div className="DJBoard__Header__Center">
                    <div className="DJBoard__KpiChip" title="Total de sugerencias recibidas">
                        <span className="DJBoard__KpiChip__Number">{suggestions.length}</span>
                        <span className="DJBoard__KpiChip__Label">Sugerencias</span>
                    </div>

                    <div className="DJBoard__KpiChip DJBoard__KpiChip--Gold" title="Canciones en cola activa">
                        <span className="DJBoard__KpiChip__Number">{activePlaylist.length}</span>
                        <span className="DJBoard__KpiChip__Label">En Cola</span>
                    </div>

                    <div className="DJBoard__KpiChip DJBoard__KpiChip--Cyan" title="Canción sonando en vivo">
                        <span className="DJBoard__KpiChip__Number">{nowPlayingSong ? '1' : '0'}</span>
                        <span className="DJBoard__KpiChip__Label">En Vivo</span>
                    </div>

                    {/* Badge de Estado SignalR */}
                    <div className={`DJBoard__WsBadge DJBoard__WsBadge--${wsStatus}`} title={`Estado SignalR: ${wsStatus}`}>
                        <span className="DJBoard__WsDot"></span>
                        <span className="DJBoard__WsText">
                            {wsStatus === 'connected' && 'SignalR En Vivo'}
                            {wsStatus === 'connecting' && 'Conectando...'}
                            {wsStatus === 'reconnecting' && 'Reconectando...'}
                            {wsStatus === 'error' && 'Sin Conexión'}
                            {wsStatus === 'closed' && 'Desconectado'}
                        </span>
                    </div>
                </div>

                {/* Acciones de Cabina */}
                <div className="DJBoard__Header__Right">
                    {/* Botón Añadir Canción Manual */}
                    <button
                        type="button"
                        className="DJBoard__HeaderBtn DJBoard__HeaderBtn--Add"
                        onClick={() => setShowManualModal(true)}
                        title="Agregar canción manual"
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span className="d-none d-xl-inline">Añadir Pista</span>
                    </button>

                    {/* Toggle Recepción de Sugerencias */}
                    <button
                        type="button"
                        className={`DJBoard__HeaderBtn ${isSubmissionActive ? 'DJBoard__HeaderBtn--Active' : 'DJBoard__HeaderBtn--Paused'}`}
                        onClick={handleToggleSubmissionActive}
                        title={isSubmissionActive ? 'Pausar recepción de sugerencias' : 'Abrir recepción de sugerencias'}
                    >
                        <i className={`bi ${isSubmissionActive ? 'bi-broadcast-pin text-success' : 'bi-pause-circle text-warning'}`}></i>
                        <span className="d-none d-xl-inline">
                            {isSubmissionActive ? 'Recepción Abierta' : 'Recepción Pausada'}
                        </span>
                    </button>

                    {/* Recargar Manual */}
                    <button
                        type="button"
                        className="DJBoard__HeaderBtn"
                        onClick={() => loadInitialData(true)}
                        disabled={isLoading}
                        title="Recargar datos desde el servidor"
                    >
                        <i className={`bi bi-arrow-clockwise ${isLoading ? 'DJBoard__Spin' : ''}`}></i>
                    </button>

                    {/* Pantalla Completa */}
                    <button
                        type="button"
                        className="DJBoard__HeaderBtn"
                        onClick={toggleFullscreen}
                        title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                    >
                        <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`}></i>
                    </button>

                    {/* Reiniciar Dinámica */}
                    <button
                        type="button"
                        className="DJBoard__HeaderBtn DJBoard__HeaderBtn--Danger"
                        onClick={handleResetDynamic}
                        title="Reiniciar dinámica de la sala"
                    >
                        <i className="bi bi-arrow-counterclockwise"></i>
                        <span className="d-none d-xl-inline">Reset</span>
                    </button>
                </div>
            </header>

            {/* --- LAYOUT DUAL SPLIT PRINCIPAL --- */}
            <main className="DJBoard__Main">
                {/* PANEL IZQUIERDO: BANDEJA DE SUGERENCIAS */}
                <PlaylistSuggestionsTable
                    suggestions={suggestions}
                    activePlaylistIds={activePlaylistIds}
                    nowPlayingId={nowPlayingSong ? nowPlayingSong.id : null}
                    onToggleFavorite={handleToggleFavorite}
                    onToggleArchive={handleToggleArchive}
                    onDeleteSuggestion={handleDeleteSuggestion}
                    onOpenManualModal={() => setShowManualModal(true)}
                    onSuggestionUpdated={(updated) => {
                        setSuggestions(prev =>
                            prev.map(item => item.id === updated.id ? { ...item, ...updated } : item)
                        );
                        setActivePlaylist(prev =>
                            prev.map(item => item.id === updated.id ? { ...item, ...updated } : item)
                        );
                    }}
                    roomId={roomId}
                    showMessage={showMessage}
                    isLoading={isLoading}
                />

                {/* PANEL DERECHO: PLAYLIST ACTIVA / COLA DE CABINA */}
                <PlaylistActiveList
                    activePlaylist={activePlaylist}
                    nowPlayingSong={nowPlayingSong}
                    onSetNowPlaying={handleSetNowPlaying}
                    onTogglePlayed={handleTogglePlayed}
                    onRemoveFromActive={handleRemoveFromActive}
                    onReorderPlaylist={handleReorderPlaylist}
                    showMessage={showMessage}
                    isLoading={isLoading}
                />
            </main>

            {/* --- MODAL DE CONFIRMACIÓN (Confirm.js) --- */}
            {showConfirmModal && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.actionToYesButton}
                    textYesButton={confirmConfig.textYesButton}
                    textNoButton={confirmConfig.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={() => {}}
                />
            )}

            {/* --- MODAL PARA AGREGAR CANCIÓN MANUAL (DJ QUICK ADD) --- */}
            {showManualModal && (
                <PlaylistManualSongModal
                    isOpen={showManualModal}
                    onClose={() => setShowManualModal(false)}
                    onSuccess={(newSong) => {
                        setSuggestions(prev => [newSong, ...prev]);
                        if (newSong.isFavorite) {
                            setActivePlaylist(prev => [...prev, newSong]);
                        }
                    }}
                    roomId={roomId}
                    defaultOperatorName="DJ Cabina"
                />
            )}
        </div>
    );
};

export default PlaylistSpecialBoard;
